// src/pages/LavorazioniAperte.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Card, TableCell, TableHeadCell } from '../components/ui';
import { downloadLavorazioniXlsx } from '../utils/exportLavorazioni';

const API_BASE = '/api';

// ---- helper generici -------------------------------------------------
const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';

  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const parseDateInputAsLocal = (value) => {
  if (!value) return null;

  const [yyyy, mm, dd] = String(value).split('-').map(Number);
  if (!yyyy || !mm || !dd) return null;

  return new Date(yyyy, mm - 1, dd);
};

const inDateRange = (valueIso, fromStr, toStr) => {
  if (!fromStr && !toStr) return true;
  if (!valueIso) return false;

  const valueDate = new Date(valueIso);
  if (Number.isNaN(valueDate.getTime())) return false;

  const valueTime = valueDate.getTime();

  if (fromStr) {
    const from = parseDateInputAsLocal(fromStr);
    if (!from) return false;
    from.setHours(0, 0, 0, 0);

    if (valueTime < from.getTime()) return false;
  }

  if (toStr) {
    const to = parseDateInputAsLocal(toStr);
    if (!to) return false;
    to.setHours(23, 59, 59, 999);

    if (valueTime > to.getTime()) return false;
  }

  return true;
};



const formatDateKey = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getFullInternalCode = (wo = {}) => {
  const base = wo.internalCode || '';
  const iteration =
    typeof wo.iteration === 'number' ? wo.iteration : 0;

  if (!base) return '-';
  return iteration > 0 ? `${base}-${iteration}` : base;
};


const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const formatTypeLabel = (type) => {
  if (type === 'manutenzione') return 'Manutenzione';
  if (type === 'intervento') return 'Intervento tecnico';
  return type || '';
};

// riconosce l’utente “Da Assegnare”
const isDaAssegnareUser = (u = {}) => {
  const username = (u.username || '').toLowerCase();
  const nome = (u.nome || '').toLowerCase();
  const cognome = (u.cognome || '').toLowerCase();
  const fullName = `${nome} ${cognome}`.trim();

  return (
    fullName === 'da assegnare' ||
    username.includes('da assegnare') ||
    username.includes('daassegnare') ||
    username.includes('da.assegnare') ||
    username.includes('da_assegnare')
  );
};

const LavorazioniAperte = () => {
  const navigate = useNavigate();

  // ---- stato generale ------------------------------------------------
  const [myRole, setMyRole] = useState(localStorage.getItem('role') || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // tabella "lavorazioni aperte"
  const [openWorks, setOpenWorks] = useState([]);
  const [users, setUsers] = useState([]);
  // ---- filtri tabella Lavorazioni aperte ------------------------------
  const [filterTextOpen, setFilterTextOpen] = useState('');
  const [filterTypeOpen, setFilterTypeOpen] = useState('tutti'); // intervento | manutenzione | tutti
  const [filterCreatedFromOpen, setFilterCreatedFromOpen] = useState('');
  const [filterCreatedToOpen, setFilterCreatedToOpen] = useState('');

  // modale riassegnazione
  const [reassignModal, setReassignModal] = useState({
    open: false,
    workOrder: null,
  });
  const [reassignForm, setReassignForm] = useState({
    dateIntervento: '',
    assignedTo: '',
  });

  // modale dettagli
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    workOrder: null,
  });

  // ---- stato calendario ----------------------------------------------
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [holidays, setHolidays] = useState([]);
  const [justifications, setJustifications] = useState([]);
  const [plannedDays, setPlannedDays] = useState([]);
  const [scheduledEvents, setScheduledEvents] = useState([]);
  const [selectedDateKey, setSelectedDateKey] = useState('');

  const isManager =
    myRole === 'admin' || myRole === 'gestione' || myRole === 'segreteria';

  const monthLabel = useMemo(() => {
    const months = [
      'Gennaio',
      'Febbraio',
      'Marzo',
      'Aprile',
      'Maggio',
      'Giugno',
      'Luglio',
      'Agosto',
      'Settembre',
      'Ottobre',
      'Novembre',
      'Dicembre',
    ];
    return `${months[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
  }, [currentMonth]);

  // -------------------------------------------------------------------
  // CARICO LAVORAZIONI APERTE (assegnate a "Da Assegnare")
  // -------------------------------------------------------------------
  const loadOpenWorks = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/work/orders`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nel recupero delle lavorazioni.');
      }
      const data = await res.json();

      const filtered = (data || []).filter((order) => {
        if (!order || order.status === 'chiusa') return false;

        const events = order.events || [];
        const lastEvent = events.length > 0 ? events[events.length - 1] : null;
        const lastEventUser = lastEvent?.assignedTo || null;
        const currentAssignee = order.assignedTo || null;

        // "Aperta" se l’assegnatario corrente è "Da Assegnare"
        // oppure l’ultimo evento è "Da Assegnare"
        if (currentAssignee && isDaAssegnareUser(currentAssignee)) return true;
        if (lastEventUser && isDaAssegnareUser(lastEventUser)) return true;

        return false;
      });

      setOpenWorks(filtered);
    } catch (err) {
      console.error('Errore loadOpenWorks:', err);
      setError(
        err.message || 'Errore nel caricamento delle lavorazioni aperte.'
      );
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------
  // CARICO UTENTI ASSEGNABILI
  // -------------------------------------------------------------------
  const loadAssignableUsers = async () => {
    try {
      const res = await authFetch(`${API_BASE}/users`);
      if (!res.ok) return;
      const data = await res.json();

      const allowedRoles = ['admin', 'gestione', 'tecnico', 'Tecnico'];
      const list = (data || []).filter((u) => {
        if (!allowedRoles.includes(u.role)) return false;

        const username = (u.username || '').toLowerCase();
        const fullName = `${u.nome || ''} ${u.cognome || ''}`.toLowerCase();

        const isDa =
          username.includes('da assegnare') ||
          username.includes('daassegnare') ||
          username.includes('da.assegnare') ||
          username.includes('da_assegnare') ||
          fullName.includes('da assegnare');

        return !isDa;
      });

      setUsers(list);
    } catch (err) {
      console.error('Errore caricamento utenti assegnabili:', err);
    }
  };

  // -------------------------------------------------------------------
  // CALENDARIO: CARICAMENTO DATI BASE (festività, assenze, interventi programmati)
  // -------------------------------------------------------------------
  const loadCalendarBase = async () => {
    try {
      const [holRes, jusRes, plannedRes] = await Promise.all([
        authFetch(`${API_BASE}/holidays`),
        authFetch(`${API_BASE}/justificativi?status=approved`),
        authFetch(`${API_BASE}/planned-days`),
      ]);

      if (holRes.ok) {
        const holidaysData = await holRes.json();
        setHolidays(holidaysData || []);
      }
      if (jusRes.ok) {
        const justData = await jusRes.json();
        setJustifications(justData || []);
      }
      if (plannedRes.ok) {
        const plannedData = await plannedRes.json();
        setPlannedDays(plannedData || []);
      }
    } catch (err) {
      console.error('Errore caricamento base calendario:', err);
      // non sovrascrivo error UI se già c’è qualcos’altro
    }
  };

  // calendario: lavorazioni programmate (WorkEvent)
  const loadScheduledEvents = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);

      const from = formatDateKey(monthStart);
      const to = formatDateKey(monthEnd);

      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      const res = await authFetch(
        `${API_BASE}/work/calendar?${params.toString()}`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel recupero delle lavorazioni programmate.'
        );
      }
      const data = await res.json();
      setScheduledEvents(data || []);
    } catch (err) {
      console.error('Errore caricamento lavorazioni programmate:', err);
    }
  };

  // -------------------------------------------------------------------
  // useEffect
  // -------------------------------------------------------------------
  useEffect(() => {
    setMyRole(localStorage.getItem('role') || null);
    loadOpenWorks();
    loadAssignableUsers();
    loadCalendarBase();
  }, []);

  useEffect(() => {
    loadScheduledEvents();
    setSelectedDateKey('');
  }, [currentMonth]);

  useEffect(() => {
    if (scheduledEvents.length === 0) {
      setSelectedDateKey('');
      return;
    }
    if (!selectedDateKey) {
      const first = scheduledEvents[0];
      setSelectedDateKey(formatDateKey(first.date));
    }
  }, [scheduledEvents, selectedDateKey]);

  // -------------------------------------------------------------------
  // MAPPE PER IL CALENDARIO
  // -------------------------------------------------------------------
  const holidaysByDate = useMemo(() => {
    const map = {};
    holidays.forEach((h) => {
      const key = formatDateKey(h.date);
      if (!key) return;
      map[key] = h;
    });
    return map;
  }, [holidays]);

  const absencesByDate = useMemo(() => {
    const map = {};
    justifications.forEach((j) => {
      const start = new Date(j.from);
      const end = new Date(j.to);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = formatDateKey(d);
        if (!key) continue;
        if (!map[key]) map[key] = [];
        map[key].push(j);
      }
    });
    return map;
  }, [justifications]);

  const scheduledByDate = useMemo(() => {
    const map = {};
    scheduledEvents.forEach((ev) => {
      const key = formatDateKey(ev.date);
      if (!key) return;

      // escludo lavorazioni con assegnatario "Da Assegnare"
      const assignee = ev.workOrder?.assignedTo || ev.assignedTo;
      if (assignee && isDaAssegnareUser(assignee)) return;

      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [scheduledEvents]);

  const plannedByDate = useMemo(() => {
    const map = {};
    plannedDays.forEach((p) => {
      const key = formatDateKey(p.date);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }, [plannedDays]);

  const daysMatrix = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const firstWeekday = (firstDay.getDay() + 6) % 7; // lunedì=0

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = [];
    let currentWeek = new Array(firstWeekday).fill(null);

    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(new Date(year, month, day));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }
    return weeks;
  }, [currentMonth]);

  const selectedScheduled = useMemo(
    () => (selectedDateKey ? scheduledByDate[selectedDateKey] || [] : []),
    [scheduledByDate, selectedDateKey]
  );

  // 👇 una sola riga per ogni lavorazione nel giorno selezionato
  const uniqueSelectedScheduled = useMemo(() => {
    const list = selectedScheduled || [];
    const seen = new Set();
    const result = [];
  
    for (const ev of list) {
      const wo = ev.workOrder || {};
      const id = wo._id || wo.id || ev.workOrder; // fallback
      if (!id) {
        result.push(ev); // se proprio non ha id, lo teniamo
        continue;
      }
      if (seen.has(id)) continue;
      seen.add(id);
      result.push(ev);
    }
  
    return result;
  }, [selectedScheduled]);
  

  const selectedPlanned = useMemo(
    () => (selectedDateKey ? plannedByDate[selectedDateKey] || [] : []),
    [plannedByDate, selectedDateKey]
  );

  const selectedDateLabel = useMemo(() => {
    if (!selectedDateKey) return '';
    const d = new Date(selectedDateKey);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }, [selectedDateKey]);

  const changeMonth = (delta) => {
    setCurrentMonth((prev) => {
      const year = prev.getFullYear();
      const month = prev.getMonth();
      return new Date(year, month + delta, 1);
    });
  };

  // -------------------------------------------------------------------
  // RIASSEGNAZIONE
  // -------------------------------------------------------------------
  const openReassignModal = (order) => {
    setReassignModal({ open: true, workOrder: order });
    setReassignForm({ dateIntervento: '', assignedTo: '' });
  };

  const closeReassignModal = () => {
    setReassignModal({ open: false, workOrder: null });
    setReassignForm({ dateIntervento: '', assignedTo: '' });
  };

  const handleReassign = async () => {
    setError('');
    const order = reassignModal.workOrder;
    if (!order) {
      setError('Lavorazione non valida per la riassegnazione.');
      return;
    }

    if (!reassignForm.dateIntervento || !reassignForm.assignedTo) {
      setError('Seleziona data intervento e utente a cui assegnare.');
      return;
    }

    try {
      const receivedAt =
        (order.receivedAt || '').slice(0, 10) ||
        new Date().toISOString().slice(0, 10);

      const res = await authFetch(`${API_BASE}/work/reassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workOrderId: order._id,
          dateReceived: receivedAt,
          dateIntervento: reassignForm.dateIntervento,
          assignedTo: reassignForm.assignedTo,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nella riassegnazione.');
      }

      const user = users.find((u) => u._id === reassignForm.assignedTo);
      const nomeTecnico = user
        ? `${user.nome || ''} ${user.cognome || ''}`.trim() || user.username
        : 'tecnico selezionato';

      setSuccess(
        `Lavorazione ${order.internalCode || ''} assegnata a ${nomeTecnico} per il ${formatDate(
          reassignForm.dateIntervento
        )}.`
      );
      closeReassignModal();
      await loadOpenWorks();
      await loadScheduledEvents();
    } catch (err) {
      console.error('Errore handleReassign (aperte):', err);
      setError(err.message || 'Errore nella richiesta di riassegnazione.');
    }
  };

  // -------------------------------------------------------------------
  // DETTAGLI
  // -------------------------------------------------------------------
  const openDetailsModal = (order) => {
      setDetailsModal({ open: true, workOrder: order });
    };

    const closeDetailsModal = () => {
      setDetailsModal({ open: false, workOrder: null });
    };

  const filteredOpenWorks = useMemo(() => {
    const text = filterTextOpen.trim().toLowerCase();

    return (openWorks || []).filter((o) => {
      if (filterTypeOpen !== 'tutti' && (o.type || '') !== filterTypeOpen) {
        return false;
      }

      // Filtro periodo su DATA/ORA CREAZIONE lavorazione
      if (
        !inDateRange(
          o.createdAt,
          filterCreatedFromOpen,
          filterCreatedToOpen
        )
      ) {
        return false;
      }

      if (!text) return true;

      const target = [
        getFullInternalCode(o),
        formatTypeLabel(o.type),
        o.companyName,
        o.place,
        o.description,
        formatDate(o.receivedAt),
        formatDateTime(o.createdAt),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return target.includes(text);
    });
  }, [
    openWorks,
    filterTextOpen,
    filterTypeOpen,
    filterCreatedFromOpen,
    filterCreatedToOpen,
  ]);


  // -------------------------------------------------------------------
  // EXPORT EXCEL
  // -------------------------------------------------------------------

  // Stesse colonne, stesso ordine del tabellone e del foglio della
  // segreteria: vedi utils/exportLavorazioni.js
  const exportOpenWorksXlsx = () => {
    if (!filteredOpenWorks.length) {
      alert('Nessuna lavorazione da esportare con i filtri attuali.');
      return;
    }

    const from = filterCreatedFromOpen || 'inizio';
    const to = filterCreatedToOpen || 'fine';

    downloadLavorazioniXlsx(
      filteredOpenWorks,
      `lavorazioni_aperte_${from}_${to}.xlsx`,
      'Lavorazioni aperte'
    );
  };



  // -------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        {/* HEADER */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              Lavorazioni aperte
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Lavorazioni non ancora assegnate a un tecnico (assegnate all&apos;utente
              &quot;Da Assegnare&quot;).
            </p>
          </div>
          <button
            onClick={() => navigate('/lavorazioni')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Torna alle lavorazioni
          </button>
        </Card>

        {/* MESSAGGI */}
        {error && (
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-200 px-4 py-3 text-sm text-emerald-700 flex items-center justify-between">
            <span>{success}</span>
            <button
              onClick={() => setSuccess('')}
              className="text-xs font-semibold text-emerald-700 hover:underline"
            >
              Chiudi
            </button>
          </div>
        )}

        {/* BOX 1: LAVORAZIONI APERTE */}
        <Card className="shadow-sm p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Lavorazioni aperte
          </h2>
          {loading && openWorks.length === 0 ? (
            <p className="text-sm text-gray-500">Caricamento...</p>
          ) : openWorks.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nessuna lavorazione aperta assegnata a &quot;Da Assegnare&quot;.
            </p>
          ) : (
            <>
              <div className="space-y-3 mb-3">
                <div className="grid md:grid-cols-[1fr_220px] gap-3">
                  <input
                    type="text"
                    placeholder="Cerca per codice, cliente, luogo, descrizione..."
                    value={filterTextOpen}
                    onChange={(e) => setFilterTextOpen(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                  <select
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    value={filterTypeOpen}
                    onChange={(e) => setFilterTypeOpen(e.target.value)}
                  >
                    <option value="tutti">Tutti i tipi</option>
                    <option value="intervento">Intervento tecnico</option>
                    <option value="manutenzione">Manutenzione</option>
                  </select>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">
                      Filtra per data creazione lavorazione
                    </p>
                    <p className="text-[11px] text-gray-500">
                      Usa un intervallo per vedere le lavorazioni aperte create in quel periodo.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[160px_160px_auto_auto] gap-2">
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-1">
                        Da
                      </label>
                      <input
                        type="date"
                        value={filterCreatedFromOpen}
                        onChange={(e) => setFilterCreatedFromOpen(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] text-gray-500 mb-1">
                        A
                      </label>
                      <input
                        type="date"
                        value={filterCreatedToOpen}
                        onChange={(e) => setFilterCreatedToOpen(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setFilterCreatedFromOpen('');
                        setFilterCreatedToOpen('');
                      }}
                      className="self-end px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                    >
                      Reset date
                    </button>

                    <button
                      type="button"
                      onClick={exportOpenWorksXlsx}
                      className="self-end px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                      Scarica Excel periodo
                    </button>
                  </div>
                </div>

                <p className="text-[11px] text-gray-500">
                  Risultati visualizzati: <span className="font-semibold">{filteredOpenWorks.length}</span>
                </p>
              </div>

              {filteredOpenWorks.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Nessun risultato con i filtri attuali.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-[11px] md:text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700">
                        <TableHeadCell className="px-2 py-1 text-left">
                          Cod. interno
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Tipo
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Cliente
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Luogo
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Descrizione
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Ricevuto il
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Creata il
                        </TableHeadCell>
                        <TableHeadCell className="px-2 py-1 text-left">
                          Azioni
                        </TableHeadCell>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOpenWorks.map((order) => (
                        <tr
                          key={order._id}
                          className="border-b last:border-b-0 hover:bg-gray-50"
                        >
                          <TableCell className="px-2 py-1 font-mono text-xs">
                            {getFullInternalCode(order)}
                          </TableCell>
                          <TableCell className="px-2 py-1 capitalize">
                            {formatTypeLabel(order.type)}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            {order.companyName || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            {order.place || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            {order.description || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1 whitespace-nowrap">
                            {formatDate(order.receivedAt)}
                          </TableCell>
                          <TableCell className="px-2 py-1 whitespace-nowrap">
                            {formatDateTime(order.createdAt)}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => openDetailsModal(order)}
                                className="px-3 py-1.5 rounded-lg text-[11px] md:text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200"
                              >
                                Dettagli
                              </button>
                              {isManager ? (
                                <button
                                  onClick={() => openReassignModal(order)}
                                  className="px-3 py-1.5 rounded-lg text-[11px] md:text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700"
                                >
                                  Assegna
                                </button>
                              ) : (
                                <span className="text-[11px] text-gray-400">
                                  Solo admin / gestione / segreteria
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Card>

        {/* BOX 2: CALENDARIO LAVORAZIONI PROGRAMMATE */}
        <Card className="shadow-sm p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Calendario lavorazioni programmate
              </h2>
              <p className="text-xs md:text-sm text-gray-500">
                Mostra tutte le lavorazioni già assegnate (escluse quelle con
                utente &quot;Da Assegnare&quot;). Clicca un giorno per vedere il
                dettaglio.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                className="px-2 py-1 rounded-lg text-xs md:text-sm text-gray-600 hover:bg-gray-100"
              >
                &larr; Mese prec.
              </button>
              <span className="font-semibold text-gray-800">{monthLabel}</span>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                className="px-2 py-1 rounded-lg text-xs md:text-sm text-gray-600 hover:bg-gray-100"
              >
                Mese succ. &rarr;
              </button>
            </div>
          </div>

          {/* legenda */}
          <div className="flex flex-wrap gap-3 text-[11px] text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-red-100 border border-red-300" />
              <span>Festività aziendale</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300" />
              <span>Assenze approvate</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" />
              <span>Lavorazioni programmate</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-orange-100 border border-orange-300" />
              <span>Interventi programmati (promemoria)</span>
            </div>
          </div>

          {/* intestazione giorni settimana */}
          <div className="grid grid-cols-7 gap-1 text-[11px] md:text-xs text-gray-500 mb-1">
            {weekdayLabels.map((w) => (
              <div key={w} className="text-center font-semibold">
                {w}
              </div>
            ))}
          </div>

          {/* calendario vero e proprio */}
          <div className="space-y-1">
            {daysMatrix.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map((day, di) => {
                  if (!day) {
                    return <div key={di} />;
                  }

                  const key = formatDateKey(day);
                  const holiday = holidaysByDate[key];
                  const absences = absencesByDate[key] || [];
                  const dayEvents = scheduledByDate[key] || [];
                  const planned = plannedByDate[key] || [];

                  const isHoliday = !!holiday;
                  const hasAbsences = absences.length > 0;
                  const hasEvents = dayEvents.length > 0;
                  const hasPlanned = planned.length > 0;
                  const isSelected = key === selectedDateKey;

                  let classes =
                    'h-12 md:h-14 rounded-lg border text-xs md:text-sm flex flex-col items-center justify-center cursor-pointer transition ';
                  if (isHoliday) {
                    classes +=
                      'bg-red-50 border-red-200 text-red-700 font-semibold';
                  } else if (hasPlanned) {
                    classes +=
                      'bg-orange-50 border-orange-200 text-orange-700 font-medium';
                  } else if (hasAbsences) {
                    classes +=
                      'bg-blue-50 border-blue-200 text-blue-700 font-medium';
                  } else if (hasEvents) {
                    classes +=
                      'bg-emerald-50 border-emerald-200 text-emerald-700 font-medium';
                  } else {
                    classes +=
                      'bg-white border-gray-200 text-gray-700 hover:bg-gray-50';
                  }

                  if (isSelected) {
                    classes += ' ring-2 ring-indigo-500 ring-offset-1';
                  }

                  return (
                    <button
                      key={di}
                      type="button"
                      onClick={() => setSelectedDateKey(key)}
                      className={classes}
                    >
                      <span>{day.getDate()}</span>
                      {!isHoliday && hasPlanned && (
                        <span className="text-[9px] mt-0.5">
                          {planned.length} int.
                        </span>
                      )}
                      {!isHoliday && !hasPlanned && hasAbsences && (
                        <span className="text-[9px] mt-0.5">
                          {absences.length} ass.
                        </span>
                      )}
                      {!isHoliday &&
                        !hasPlanned &&
                        !hasAbsences &&
                        hasEvents && (
                          <span className="text-[9px] mt-0.5">
                            {dayEvents.length} lav.
                          </span>
                        )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* dettaglio giorno selezionato */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-800">
                Lavorazioni programmate nel giorno selezionato
              </h3>
              <span className="text-[11px] text-gray-500 capitalize">
                {selectedDateLabel || 'Nessun giorno selezionato'}
              </span>
            </div>

            {uniqueSelectedScheduled.length === 0 ? (
              <p className="text-xs text-gray-500">
                Nessuna lavorazione programmata per il giorno selezionato.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-[11px] md:text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <TableHeadCell className="px-2 py-1 text-left">
                        Cod. interno
                      </TableHeadCell>
                      <TableHeadCell className="px-2 py-1 text-left">
                        Tipo
                      </TableHeadCell>
                      <TableHeadCell className="px-2 py-1 text-left">
                        Assegnato a
                      </TableHeadCell>
                      <TableHeadCell className="px-2 py-1 text-left">
                        Cliente
                      </TableHeadCell>
                      <TableHeadCell className="px-2 py-1 text-left">
                        Luogo
                      </TableHeadCell>
                      <TableHeadCell className="px-2 py-1 text-left">
                        Descrizione
                      </TableHeadCell>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueSelectedScheduled.map((ev) => {
                      const wo = ev.workOrder || {};
                      const assignee = ev.assignedTo || wo.assignedTo;
                      const fullName = `${assignee?.nome || ''} ${
                        assignee?.cognome || ''
                      }`.trim();
                      const assigneeLabel = fullName || assignee?.username || '-';
                    
                      return (
                        <tr
                          key={ev._id}
                          className="border-b last:border-b-0 hover:bg-gray-50"
                        >
                          <TableCell className="px-2 py-1 font-mono text-xs">
                            {getFullInternalCode(wo)}
                          </TableCell>
                          <TableCell className="px-2 py-1 capitalize">
                            {formatTypeLabel(wo.type)}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            {assigneeLabel}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            {wo.companyName || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            {wo.place || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            {wo.description || '-'}
                          </TableCell>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            


            {selectedPlanned.length > 0 && (
              <div className="mt-3">
                <h4 className="text-xs font-semibold text-gray-700 mb-1">
                  Interventi programmati (promemoria) nel giorno selezionato
                </h4>
                <ul className="text-[11px] text-gray-600 list-disc ml-4 space-y-0.5">
                  {selectedPlanned.map((p) => (
                    <li key={p._id}>{p.description}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>

        {/* BOTTONE INDIETRO MOBILE */}
        <div className="md:hidden flex justify-start">
          <button
            onClick={() => navigate('/lavorazioni')}
            className="inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Torna alle lavorazioni
          </button>
        </div>
      </div>

      {/* MODALE RIASSEGNAZIONE */}
      {reassignModal.open && reassignModal.workOrder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Assegna lavorazione
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              {reassignModal.workOrder.internalCode} –{' '}
              {reassignModal.workOrder.companyName}
            </p>

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Data intervento
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs"
                  value={reassignForm.dateIntervento}
                  onChange={(e) =>
                    setReassignForm((prev) => ({
                      ...prev,
                      dateIntervento: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Assegna a
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs"
                  value={reassignForm.assignedTo}
                  onChange={(e) =>
                    setReassignForm((prev) => ({
                      ...prev,
                      assignedTo: e.target.value,
                    }))
                  }
                >
                  <option value="">Seleziona tecnico...</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {`${u.nome || ''} ${u.cognome || ''}`.trim() ||
                        u.username}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={closeReassignModal}
                className="px-3 py-1.5 rounded-lg text-xs text-gray-600 hover:bg-gray-100"
              >
                Annulla
              </button>
              <button
                onClick={handleReassign}
                className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
              >
                Conferma assegnazione
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE DETTAGLI */}
      {detailsModal.open && detailsModal.workOrder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Dettagli lavorazione
                </h3>
                <p className="text-xs text-gray-500">
                  Codice interno{' '}
                  <span className="font-mono font-semibold">
                    {detailsModal.workOrder.internalCode}
                  </span>
                </p>
              </div>
              <button
                onClick={closeDetailsModal}
                className="text-xs text-gray-500 hover:text-gray-800"
              >
                Chiudi
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4 text-xs text-gray-700">
              <div className="space-y-1">
                <p>
                  <span className="font-semibold">Cliente:</span>{' '}
                  {detailsModal.workOrder.companyName || '-'}
                </p>
                <p>
                  <span className="font-semibold">Luogo:</span>{' '}
                  {detailsModal.workOrder.place || '-'}
                </p>
                <p>
                  <span className="font-semibold">Tipo:</span>{' '}
                  {formatTypeLabel(detailsModal.workOrder.type)}
                </p>
                <p>
                  <span className="font-semibold">Creata il:</span>{' '}
                  {formatDateTime(detailsModal.workOrder.createdAt)}
                </p>
                <p>
                  <span className="font-semibold">Ricevuto il:</span>{' '}
                  {formatDate(detailsModal.workOrder.receivedAt)}
                </p>
                {detailsModal.workOrder.type === 'intervento' && (
                  <p>
                    <span className="font-semibold">Stato preventivo:</span>{' '}
                    {detailsModal.workOrder.preventivoStatus || '-'}
                  </p>
                )}
              </div>

              {detailsModal.workOrder.type === 'manutenzione' && (
                <>
                  <p>
                    <span className="font-semibold">Manutenzione con preventivo:</span>{' '}
                    {detailsModal.workOrder.manutenzioneConPreventivo ? 'Sì' : 'No'}
                  </p>
                  {detailsModal.workOrder.manutenzioneConPreventivo && (
                    <p>
                      <span className="font-semibold">Rif. preventivo:</span>{' '}
                      {detailsModal.workOrder.rifPreventivo || '-'}
                    </p>
                  )}
                </>
              )}
              
              {detailsModal.workOrder.type === 'intervento' && (
                <p>
                  <span className="font-semibold">Rif. preventivo:</span>{' '}
                  {detailsModal.workOrder.rifPreventivo || '-'}
                </p>
              )}
              

              <div className="space-y-1">
                <p>
                  <span className="font-semibold">Tipo apparecchiatura:</span>{' '}
                  {detailsModal.workOrder.equipmentType || '-'}
                </p>
                <p>
                  <span className="font-semibold">Produttore:</span>{' '}
                  {detailsModal.workOrder.equipmentMaker || '-'}
                </p>
                <p>
                  <span className="font-semibold">Modello:</span>{' '}
                  {detailsModal.workOrder.equipmentModel || '-'}
                </p>
                <p>
                  <span className="font-semibold">Seriale:</span>{' '}
                  {detailsModal.workOrder.equipmentSerial || '-'}
                </p>
                <p>
                  <span className="font-semibold">Inventario:</span>{' '}
                  {detailsModal.workOrder.equipmentInventory || '-'}
                </p>
              </div>

              <div className="md:col-span-2 mt-2">
                <p className="font-semibold mb-1">Descrizione:</p>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {detailsModal.workOrder.description || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LavorazioniAperte;
